import React, { useMemo } from 'react';
import { GridColumn, GridCustomCellProps, GridCustomHeaderCellProps } from '@progress/kendo-react-grid';
import cn from 'clsx';
import useGridCustomCell from '@components-clean/core/grid/custom-cell';
import FilterCustomCell, {
    ExtendedGridCustomFilterCellProps,
    FilterCellValueType,
} from '@components-clean/core/grid/filter-custom-cell';
import {
    GridColumnProps,
    REORDER_COLUMN_ID,
    ROW_MENU_ACTION_COLUMN_ID,
} from '@components-clean/core/grid/grid.interface';

const { HeaderCell, StandardCell, CheckBoxHeaderCell } = useGridCustomCell();

export const renderGridColumn = (
    columns: GridColumnProps[],
    reorderActive: boolean,
    totalSelectableRows: number,
    isParentLocked?: boolean,
    setWidth?: (minWidth: number | undefined, isCustom?: boolean | undefined) => number,
    clientGrid?: boolean,
    showPlaceholder?: boolean,
    selectedState?: {
        [id: string]: boolean | number[];
    },
) => {
    const standardCellCache: Record<string, React.FC<GridCustomCellProps>> = {};
    const headerCellCache: Record<string, React.FC<GridCustomHeaderCellProps>> = {};

    // Hidden columns can still be locked (e.g. lockedByDefault but showByDefault: false), so exclude
    // them here — otherwise a hidden locked column can be treated as the last locked column, and the
    // gray separator ends up attached to a column that never renders instead of the last visible one.
    const lockedColumns = columns.filter((column) => column.locked && column.show);
    const lastLockedColumnField = lockedColumns.length ? lockedColumns[lockedColumns.length - 1]?.field : null;

    if (showPlaceholder === undefined) {
        showPlaceholder = true;
    }
    let selectedRowCount = 0;
    if (selectedState && Object.keys(selectedState).length > 0)
        selectedRowCount = Object.keys(selectedState).filter((k) => selectedState[k] === true).length;

    columns.forEach((column) => {
        const key = column.field ?? column.title ?? column.id;
        if (key && !standardCellCache[key]) {
            standardCellCache[key] = (props: GridCustomCellProps) => (
                <StandardCell
                    {...props}
                    maxLength={column.maxLength}
                    activeField={column.activeField}
                    filterType={column.filter}
                    emptyDisplayText={column.emptyDisplayText}
                />
            );

            if (!headerCellCache[key]) {
                headerCellCache[key] = (props: GridCustomHeaderCellProps) =>
                    column.columnType === 'checkbox' ? (
                        <CheckBoxHeaderCell
                            {...props}
                            title={props.title}
                            selectedRowCount={selectedRowCount}
                            totalSelectableRows={totalSelectableRows}
                        >
                            {props.children}
                        </CheckBoxHeaderCell>
                    ) : (
                        <HeaderCell
                            {...props}
                            lockedColumn={column.locked === true && column.id !== REORDER_COLUMN_ID}
                            multiColumnHeader={!!column.children?.length}
                            title={props.title}
                            showHeaderToolTip={column.showHeaderToolTip}
                        >
                            {props.children}
                        </HeaderCell>
                    );
            }
        }
    });

    const memoizedFilterCellCache = useMemo(() => {
        const cache: Record<string, React.FC<ExtendedGridCustomFilterCellProps>> = {};
        columns.forEach((column) => {
            const key = column.field ?? column.title ?? column.id;
            if (key && !cache[key]) {
                cache[key] = ({
                    booleanValues,
                    filterType,
                    onChange,
                    operators,
                    value,
                    field,
                    thProps,
                }: ExtendedGridCustomFilterCellProps) => {
                    const {
                        lovData,
                        lovFilterContains,
                        lovMultiSelect,
                        filterKey,
                        comparisonFilterKey,
                        ragColumn,
                        filterDisabled,
                        singleValueRagColumn,
                        multiValueTypeColumn,
                        filterClassName,
                        evaluationColumn,
                        hideAdvancedFilter,
                    } = column;

                    return (
                        <FilterCustomCell
                            thProps={thProps}
                            field={field}
                            filterType={filterType}
                            onChange={onChange}
                            value={value as FilterCellValueType}
                            clientGrid={clientGrid}
                            booleanValues={booleanValues}
                            operators={operators}
                            lovData={lovData}
                            lovFilterContains={lovFilterContains}
                            lovMultiSelect={lovMultiSelect}
                            filterKey={filterKey}
                            comparisonFilterKey={comparisonFilterKey}
                            ragColumn={ragColumn}
                            filterDisabled={filterDisabled}
                            singleValueRagColumn={singleValueRagColumn}
                            multiValueTypeColumn={multiValueTypeColumn}
                            showPlaceholder={showPlaceholder}
                            filterClassName={filterClassName}
                            evaluationColumn={evaluationColumn}
                            hideAdvancedFilter={hideAdvancedFilter}
                        />
                    );
                };
            }
        });
        return cache;
    }, [columns]);

    const memoizedColumnWidth = useMemo(() => {
        return columns.reduce(
            (widthCache, column) => {
                const key = column.field ?? column.title ?? column.id;
                if (key && setWidth) {
                    const childrenMinWidthTotal = column.children
                        ?.filter((col) => col.show)
                        ?.reduce((n, { minWidth }) => n + (minWidth ?? 0), 0);

                    widthCache[key] = setWidth(
                        childrenMinWidthTotal || (column.minWidth ?? Number(column.width)),
                        column.columnType === 'checkbox' || column.customColumn || column.lockWidth,
                    );
                }
                return widthCache;
            },
            {} as Record<string, number>,
        );
    }, [columns, setWidth]);

    return columns.map((column) => {
        const key = column.field ?? column.title ?? column.id;
        const cachedStandardCell = key ? standardCellCache[key] : undefined;
        const cachedHeaderCell = key ? headerCellCache[key] : undefined;

        const cachedFilterCell = key ? memoizedFilterCellCache[key] : undefined;

        const columnClassName = cn(column.className, {
            'last-locked-column':
                lastLockedColumnField &&
                lastLockedColumnField === column.field &&
                column.id !== REORDER_COLUMN_ID &&
                column.id !== ROW_MENU_ACTION_COLUMN_ID,
        });

        const cells = {
            ...column.cells,
            data: column.cells?.data ?? cachedStandardCell,
            headerCell: column.cells?.headerCell ?? cachedHeaderCell,
            filterCell:
                column.filterable || column.filterable === undefined
                    ? (column.cells?.filterCell ?? cachedFilterCell)
                    : undefined,
        };

        const locked = !!(column.locked || isParentLocked);
        const width = setWidth && key ? memoizedColumnWidth[key] : undefined;

        // Bug 65141 (issue 2), column and headers become misaligned when a locked column at the start
        // of a sequence of locked columns is shrank (doesn't matter if it's made larger) and then the data prop changes
        // either through filter, sort or virtual scrolling. Due to this we opted to make it so that locked columns
        // can not be shrank past their default width. Amd because we re-draw the grid when locked cols change, we don't have
        // to track the columns width and can just use the default. This is also why I've opted to override the minResizableWidth
        // when a column is locked, just incase the passed in min width is less than the default.

        // Kendo have removed the minResizableWidth prop, we now only have width, minWidth and maxWidth.
        // Use minWidth value from column in width calculation above but no longer use the value for a columns
        // final minWidth value (unless locked). This allows columns to calculate the correct width while still being resizeable
        const minResizable = locked ? width || column.minWidth : 60;

        return (
            // We have to be careful when setting props on these columns, if they differ from the Grid column config
            // then the Kendo events will give us columns with the props below, then we end up overwriting column
            // config with these values losing the "defaults" from the parents

            // If you do update these, make sure to check grid\index.tsx updateReorderedColumns function, where we
            // update state based on Kendo events
            <GridColumn
                {...column}
                hidden={!column.show}
                locked={locked}
                key={key}
                className={columnClassName}
                columnMenu={clientGrid ? undefined : reorderActive ? undefined : column.columnMenu}
                cells={cells}
                minWidth={minResizable}
                width={width}
            >
                {column.children?.length
                    ? renderGridColumn(
                          column.children,
                          reorderActive,
                          totalSelectableRows,
                          locked,
                          setWidth,
                          clientGrid,
                          showPlaceholder,
                      )
                    : undefined}
            </GridColumn>
        );
    });
};
